import React, { useEffect, useMemo, useState } from 'react';
import {
  stockPortfolioService,
  StockInstrument,
  StockPosition,
  PlaceStockOrderRequest,
} from '../../services/stockPortfolioService';

type OrderSide = 'BUY' | 'SELL';
type OrderType = 'MARKET' | 'LIMIT' | 'STOP';

interface StockOrderModalProps {
  configId: number;
  currency: string;
  /** DEMO (practice money) or LIVE (real money) — drives the warning banner. */
  environment: string;
  /** Open positions, used for sell prefill and price estimates. */
  positions: StockPosition[];
  /** Prefill (e.g. Sell button on a position row). */
  initialSide?: OrderSide;
  initialTicker?: string;
  onClose: () => void;
  /** Called after a successful placement so the page can refresh. */
  onPlaced: () => void;
}

/** Above admin mobile bottom nav (z-index 9999). */
const OVERLAY_Z = 10040;

function buildOverlayStyle(isNarrow: boolean): React.CSSProperties {
  return {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: isNarrow ? 'flex-end' : 'center',
    justifyContent: 'center',
    zIndex: OVERLAY_Z,
    padding: isNarrow ? 0 : 16,
  };
}

function buildModalStyle(isNarrow: boolean): React.CSSProperties {
  return {
    background: '#fff',
    borderRadius: isNarrow ? '14px 14px 0 0' : 14,
    padding: 20,
    paddingBottom: isNarrow ? 'calc(20px + env(safe-area-inset-bottom, 0px))' : 20,
    width: '100%',
    maxWidth: 460,
    maxHeight: isNarrow ? 'min(92dvh, 92vh)' : '90vh',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.35)',
    boxSizing: 'border-box',
    margin: isNarrow ? '0 auto' : undefined,
  };
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: '#475569',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: 13,
  color: '#1e293b',
  boxSizing: 'border-box',
};

function displayTicker(ticker: string | null | undefined): string {
  if (!ticker) return '—';
  return ticker.split('_')[0];
}

function fmtMoney(n: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

const StockOrderModal: React.FC<StockOrderModalProps> = ({
  configId,
  currency,
  environment,
  positions,
  initialSide = 'BUY',
  initialTicker = '',
  onClose,
  onPlaced,
}) => {
  const isLive = environment === 'LIVE';
  const [isNarrow, setIsNarrow] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false),
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [side, setSide] = useState<OrderSide>(initialSide);
  const [type, setType] = useState<OrderType>('MARKET');
  const [ticker, setTicker] = useState(initialTicker);
  const [search, setSearch] = useState('');
  const [quantity, setQuantity] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [timeValidity, setTimeValidity] = useState<'DAY' | 'GOOD_TILL_CANCEL'>('DAY');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [instruments, setInstruments] = useState<StockInstrument[]>([]);
  const [instrumentsLoading, setInstrumentsLoading] = useState(false);

  // The instruments catalog is only needed to search for a NEW instrument to buy;
  // sells are limited to instruments already held.
  useEffect(() => {
    if (side !== 'BUY') return;
    let cancelled = false;
    setInstrumentsLoading(true);
    stockPortfolioService
      .getInstruments(configId)
      .then(list => {
        if (!cancelled) setInstruments(list);
      })
      .catch(() => {
        if (!cancelled) setInstruments([]);
      })
      .finally(() => {
        if (!cancelled) setInstrumentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configId, side]);

  const heldPosition = positions.find(p => p.ticker === ticker) || null;

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return instruments
      .filter(
        i =>
          i.ticker.toLowerCase().includes(q) ||
          (i.name || '').toLowerCase().includes(q) ||
          (i.shortName || '').toLowerCase().includes(q),
      )
      .slice(0, 15);
  }, [instruments, search]);

  const selectedInstrument = instruments.find(i => i.ticker === ticker) || null;

  const qty = Number(quantity);
  const priceHint =
    type === 'LIMIT'
      ? Number(limitPrice)
      : type === 'STOP'
        ? Number(stopPrice)
        : Number(heldPosition?.currentPrice ?? 0);
  const estimatedValue = Number.isFinite(qty) && qty > 0 && priceHint > 0 ? qty * priceHint : null;

  const validationError = useMemo(() => {
    if (!ticker) return 'Choose an instrument';
    if (!Number.isFinite(qty) || qty <= 0) return 'Enter a quantity greater than zero';
    if (side === 'SELL' && heldPosition && qty > Number(heldPosition.quantity)) {
      return `You only hold ${heldPosition.quantity} — reduce the quantity`;
    }
    if (type === 'LIMIT' && !(Number(limitPrice) > 0)) return 'Enter a limit price';
    if (type === 'STOP' && !(Number(stopPrice) > 0)) return 'Enter a stop price';
    return null;
  }, [ticker, qty, side, heldPosition, type, limitPrice, stopPrice]);

  const submit = async () => {
    if (validationError) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: PlaceStockOrderRequest = {
        ticker,
        side,
        type,
        quantity: qty,
        ...(type === 'LIMIT' ? { limitPrice: Number(limitPrice), timeValidity } : {}),
        ...(type === 'STOP' ? { stopPrice: Number(stopPrice), timeValidity } : {}),
      };
      await stockPortfolioService.placeStockOrder(configId, payload);
      onPlaced();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Order failed');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={buildOverlayStyle(isNarrow)} onClick={onClose} role="dialog" aria-modal="true">
      <div style={buildModalStyle(isNarrow)} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>
            {side === 'BUY' ? 'Buy' : 'Sell'} {ticker ? displayTicker(ticker) : 'stock'}
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: 18, color: '#64748b', cursor: 'pointer' }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Environment banner */}
        <div
          role="alert"
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            marginBottom: 14,
            fontSize: 12,
            fontWeight: 700,
            background: isLive ? '#fee2e2' : '#fef3c7',
            border: `1px solid ${isLive ? '#fca5a5' : '#fcd34d'}`,
            color: isLive ? '#b91c1c' : '#92400e',
          }}
        >
          {isLive
            ? 'LIVE account — this order uses real money on your Trading 212 account.'
            : 'Demo / practice account — no real money is involved.'}
        </div>

        {/* Side toggle */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {(['BUY', 'SELL'] as OrderSide[]).map(s => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setSide(s);
                setConfirming(false);
              }}
              style={{
                flex: 1,
                padding: '8px 0',
                borderRadius: 8,
                border: '1px solid',
                borderColor: side === s ? (s === 'BUY' ? '#15803d' : '#b91c1c') : '#cbd5e1',
                background: side === s ? (s === 'BUY' ? '#dcfce7' : '#fee2e2') : '#fff',
                color: side === s ? (s === 'BUY' ? '#15803d' : '#b91c1c') : '#64748b',
                fontWeight: 800,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Instrument picker */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Instrument</label>
          {side === 'SELL' ? (
            <select
              value={ticker}
              onChange={e => setTicker(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select a held position…</option>
              {positions.map(p => (
                <option key={p.ticker} value={p.ticker}>
                  {displayTicker(p.ticker)} — holding {p.quantity}
                </option>
              ))}
            </select>
          ) : ticker ? (
            <div
              style={{
                ...inputStyle,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc',
              }}
            >
              <span style={{ fontWeight: 700 }}>
                {displayTicker(ticker)}
                {selectedInstrument?.name ? ` — ${selectedInstrument.name}` : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  setTicker('');
                  setSearch('');
                  setConfirming(false);
                }}
                style={{ border: 'none', background: 'none', color: '#2563eb', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={instrumentsLoading ? 'Loading instrument list…' : 'Search by ticker or company name'}
                style={inputStyle}
                disabled={instrumentsLoading}
              />
              {searchResults.length > 0 && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>
                  {searchResults.map(i => (
                    <button
                      key={i.ticker}
                      type="button"
                      onClick={() => {
                        setTicker(i.ticker);
                        setConfirming(false);
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '8px 10px',
                        border: 'none',
                        borderBottom: '1px solid #f1f5f9',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 13,
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#1e293b' }}>{displayTicker(i.ticker)}</span>
                      <span style={{ color: '#64748b', marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {i.name || i.shortName || ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Order type */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Order type</label>
          <select
            value={type}
            onChange={e => {
              setType(e.target.value as OrderType);
              setConfirming(false);
            }}
            style={inputStyle}
          >
            <option value="MARKET">Market — execute at current price</option>
            <option value="LIMIT">Limit — execute at your price or better</option>
            <option value="STOP">Stop — trigger when price crosses a level</option>
          </select>
        </div>

        {/* Quantity */}
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>
            Quantity (shares)
            {side === 'SELL' && heldPosition ? ` — you hold ${heldPosition.quantity}` : ''}
          </label>
          <input
            type="number"
            min="0"
            step="any"
            value={quantity}
            onChange={e => {
              setQuantity(e.target.value);
              setConfirming(false);
            }}
            placeholder="e.g. 0.5"
            style={inputStyle}
          />
          {selectedInstrument?.minTradeQuantity != null && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
              Minimum trade quantity: {selectedInstrument.minTradeQuantity}
            </div>
          )}
        </div>

        {/* Prices */}
        {type === 'LIMIT' && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Limit price ({currency})</label>
            <input
              type="number"
              min="0"
              step="any"
              value={limitPrice}
              onChange={e => {
                setLimitPrice(e.target.value);
                setConfirming(false);
              }}
              style={inputStyle}
            />
          </div>
        )}
        {type === 'STOP' && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Stop price ({currency})</label>
            <input
              type="number"
              min="0"
              step="any"
              value={stopPrice}
              onChange={e => {
                setStopPrice(e.target.value);
                setConfirming(false);
              }}
              style={inputStyle}
            />
          </div>
        )}
        {type !== 'MARKET' && (
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Valid until</label>
            <select
              value={timeValidity}
              onChange={e => setTimeValidity(e.target.value as 'DAY' | 'GOOD_TILL_CANCEL')}
              style={inputStyle}
            >
              <option value="DAY">End of trading day</option>
              <option value="GOOD_TILL_CANCEL">Until cancelled</option>
            </select>
          </div>
        )}

        {/* Estimate */}
        {estimatedValue != null && (
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>
            Estimated {side === 'BUY' ? 'cost' : 'proceeds'}: <strong>{fmtMoney(estimatedValue, currency)}</strong>
            {type === 'MARKET' && ' (based on last known price — actual fill may differ)'}
          </div>
        )}

        {(validationError || error) && (
          <div style={{ padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 12, marginBottom: 12 }}>
            {error || validationError}
          </div>
        )}

        {/* Actions: two-step confirm */}
        {!confirming ? (
          <button
            type="button"
            disabled={!!validationError}
            onClick={() => setConfirming(true)}
            style={{
              width: '100%',
              padding: '10px 0',
              borderRadius: 999,
              border: 'none',
              background: validationError ? '#cbd5e1' : side === 'BUY' ? '#15803d' : '#b91c1c',
              color: '#fff',
              fontWeight: 800,
              fontSize: 14,
              cursor: validationError ? 'not-allowed' : 'pointer',
            }}
          >
            Review {side === 'BUY' ? 'buy' : 'sell'} order
          </button>
        ) : (
          <div>
            <div
              style={{
                padding: '10px 12px',
                background: isLive ? '#fef2f2' : '#f8fafc',
                border: `1px solid ${isLive ? '#fecaca' : '#e2e8f0'}`,
                borderRadius: 8,
                fontSize: 13,
                color: '#334155',
                marginBottom: 10,
              }}
            >
              {side} {qty} × {displayTicker(ticker)} as a {type.toLowerCase()} order
              {type === 'LIMIT' ? ` at ${fmtMoney(Number(limitPrice), currency)}` : ''}
              {type === 'STOP' ? ` triggered at ${fmtMoney(Number(stopPrice), currency)}` : ''}
              {isLive
                ? '. This places a REAL-MONEY order on your LIVE Trading 212 account.'
                : '. This places an order on your demo/practice account.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 999,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#334155',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={submitting}
                style={{
                  flex: 2,
                  padding: '10px 0',
                  borderRadius: 999,
                  border: 'none',
                  background: side === 'BUY' ? '#15803d' : '#b91c1c',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: submitting ? 'wait' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Placing order…' : `Confirm ${side.toLowerCase()}${isLive ? ' (LIVE)' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockOrderModal;
